import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  FiX,
  FiPlus,
  FiMenu,
  FiPackage,
  FiSearch,
  FiInfo,
  FiLayers,
  FiCheckCircle,FiSave
} from "react-icons/fi";
import SafeIcon from "../common/SafeIcon"; // Assuming SafeIcon is available

// --- START: SortableItem ที่ออกแบบใหม่ ---
const SortableItem = ({ id, title, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group flex items-center justify-between p-2 mb-2 bg-white border border-slate-200 rounded-lg shadow-sm"
    >
      <div className="flex items-center gap-2 min-w-0">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab p-1 text-slate-400 hover:text-slate-600"
        >
          <FiMenu />
        </button>
        <span className="text-sm text-slate-800 truncate" title={title}>
          {title}
        </span>
      </div>
      <button
        onClick={() => onRemove(id)}
        className="p-1 text-slate-400 hover:text-red-500 flex-shrink-0 opacity-50 group-hover:opacity-100 transition-opacity"
      >
        <FiX />
      </button>
    </div>
  );
};
// --- END: SortableItem ---

const ReferenceModal = ({
  isOpen,
  onClose,
  allMeetings,
  activeMeetingId,
  currentReferencedIds,
  onSave,
}) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [availableMeetings, setAvailableMeetings] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (isOpen) {
      setSelectedIds(currentReferencedIds || []);
      setSearchTerm("");
    }
  }, [currentReferencedIds, isOpen]);

  useEffect(() => {
    const safeAllMeetings = allMeetings || [];
    let baseAvailable = safeAllMeetings.filter(
      (m) => m.id !== activeMeetingId && !selectedIds.includes(m.id)
    );
    if (searchTerm.trim() !== "") {
      baseAvailable = baseAvailable.filter((m) =>
        m.title.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    setAvailableMeetings(baseAvailable);
  }, [allMeetings, activeMeetingId, selectedIds, searchTerm]);

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setSelectedIds((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleAdd = (id) => {
    setSelectedIds((prevIds) => [...prevIds, id]);
  };

  const handleRemove = (id) => {
    setSelectedIds((prevIds) =>
      prevIds.filter((currentId) => currentId !== id)
    );
  };

  const handleSave = () => {
    onSave(selectedIds);
    onClose();
  };

  if (!isOpen) return null;

  const meetingsMap = new Map((allMeetings || []).map((m) => [m.id, m]));

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4">
        <motion.div
          className="bg-gradient-to-b from-white to-slate-50 rounded-xl shadow-2xl w-full max-w-4xl h-full md:h-[85vh] md:max-h-[800px] flex flex-col"
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          {/* Header */}
          <div className="p-4 border-b border-slate-200 flex justify-between items-center flex-shrink-0">
            <h2 className="text-xl font-bold text-green-800 flex items-center gap-2">
              <SafeIcon icon={FiLayers} />
              จัดการรายการอ้างอิง
            </h2>
            <button
              onClick={onClose}
              className="p-1.5 rounded-full text-slate-500 hover:text-green-600 hover:bg-green-100 transition-colors"
            >
              <FiX size={24} />
            </button>
          </div>

          {/* Info Box */}
          <div className="flex-shrink-0 p-3 mx-4 mt-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
            <SafeIcon
              icon={FiInfo}
              className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5"
            />
            <div>
              <p className="font-semibold text-green-800 text-sm">
                การอ้างอิงข้อมูล
              </p>
              <p className="text-xs text-green-700 mt-1">
                นำเนื้อหาจากการประชุมอื่นมาวิเคราะห์ร่วมกัน
                เหมาะสำหรับโปรเจกต์ต่อเนื่องหรือการประชุมที่ถูกแบ่งไฟล์
                <strong> (ลากเพื่อจัดลำดับความสำคัญ)</strong>
              </p>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 flex flex-col md:flex-row gap-4 p-4 overflow-y-auto">
            {/* Left Panel: Available Meetings */}
            <div className="flex flex-col bg-white border border-slate-200 rounded-lg md:w-1/2 shadow-sm">
              <h3 className="p-3 bg-slate-50 font-semibold text-slate-700 border-b border-slate-200 flex-shrink-0">
                รายการทั้งหมด ({availableMeetings.length})
              </h3>
              <div className="p-3 border-b border-slate-200 flex-shrink-0">
                <div className="relative">
                  <SafeIcon
                    icon={FiSearch}
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5"
                  />
                  <input
                    type="text"
                    placeholder="ค้นหา..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 text-sm bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400"
                  />
                </div>
              </div>
              <div className="p-2 overflow-y-auto">
                {availableMeetings.length > 0 ? (
                  availableMeetings.map((meeting) => (
                    <div
                      key={meeting.id}
                      className="flex items-center justify-between p-2 mb-1 hover:bg-slate-100 rounded-lg group"
                    >
                      <span
                        className="text-sm text-slate-800 truncate"
                        title={meeting.title}
                      >
                        {meeting.title}
                      </span>
                      <button
                        onClick={() => handleAdd(meeting.id)}
                        className="p-1.5 text-green-600 hover:text-green-800 flex-shrink-0 bg-green-100 rounded-md opacity-0 group-hover:opacity-100 transition-all hover:scale-110"
                        title="เพิ่ม"
                      >
                        <FiPlus />
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="flex flex-col items-center justify-center text-slate-400 text-center p-4 min-h-[100px]">
                    <FiPackage size={32} className="mb-2 text-slate-300" />
                    <p className="font-semibold text-slate-600">
                      {searchTerm ? "ไม่พบผลลัพธ์" : "ไม่มีรายการเหลือ"}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Right Panel: Selected for Reference */}
            <div className="flex flex-col bg-white border border-slate-200 rounded-lg md:w-1/2 shadow-sm">
              <h3 className="p-3 bg-slate-50 font-semibold text-slate-700 border-b border-slate-200 flex-shrink-0">
                รายการที่เลือก ({selectedIds.length})
              </h3>
              <div className="p-3 overflow-y-auto">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={selectedIds}
                    strategy={verticalListSortingStrategy}
                  >
                    {selectedIds.map((id) => (
                      <SortableItem
                        key={id}
                        id={id}
                        title={meetingsMap.get(id)?.title || "Unknown"}
                        onRemove={handleRemove}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
                {selectedIds.length === 0 && (
                  <div className="flex flex-col items-center justify-center text-slate-400 text-center p-4 min-h-[100px]">
                    <FiCheckCircle size={32} className="mb-2 text-slate-300" />
                    <p className="font-semibold text-slate-600">
                      ยังไม่มีรายการที่เลือก
                    </p>
                    <p className="text-xs">กดปุ่ม "+" จากฝั่งซ้ายเพื่อเพิ่ม</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-slate-200 flex justify-end gap-3 flex-shrink-0">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-100 transition-colors"
            >
              ยกเลิก
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors shadow-sm flex items-center gap-1.5"
            >
              <SafeIcon icon={FiSave} /> บันทึกการเปลี่ยนแปลง
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ReferenceModal;
